<template>
    <aside
        ref="toolbar"
        class="toolbar"
        :class="toolbarClasses"
        :style="toolbarStyles"
        @click.stop
    >
        <button ref="toolbarHandle" class="toolbar__collapse" @click="onToggle">
            <font-awesome-icon class="toolbar-caret" :icon="['fas', toolbarCaret]" />
        </button>

        <div class="toolbar__content">
            <slot />
        </div>
    </aside>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { TOGGLE_TOOLBAR } from '@/store/types/mutation-types';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'toolbar',

    props: {
        id: {
            type: String,
            required: true,
        },

        collapseOffset: {
            type: Number,
            default: 0,
        },

        side: {
            type: String,
            default: 'right',
        },
    },

    data() {
        return {
            width: 0,
            borderWidth: 0,
            handleWidth: 0,
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            toolbarVisibility: state => state.page.toolbarVisibility,
        }),

        isLeft() {
            return this.side === 'left';
        },

        isRight() {
            return this.side === 'right';
        },

        isVisible() {
            return this.toolbarVisibility[this.id];
        },

        toolbarCaret() {
            if (this.isLeft) {
                return this.isVisible ? 'caret-left' : 'caret-right';
            }

            return this.isVisible ? 'caret-right' : 'caret-left';
        },

        toolbarClasses() {
            return {
                [STATE_CLASSES.VISIBLE]: this.isVisible && !this.previewMode,
                'toolbar--left': this.isLeft,
                'toolbar--right': this.isRight,
            };
        },

        toolbarStyles() {
            let xPos;

            switch (true) {
                case this.isLeft && this.previewMode:
                    xPos = (-this.width) - this.handleWidth;
                    break;

                case this.isLeft && !this.isVisible:
                    xPos = (-this.width) + this.borderWidth + this.collapseOffset;
                    break;

                case this.isRight && this.previewMode:
                    xPos = this.width + this.handleWidth;
                    break;

                case this.isRight && !this.isVisible:
                    xPos = this.width - this.borderWidth - this.collapseOffset;
                    break;

                default:
                    xPos = 0;
            }

            return {
                transform: `translateX(${ xPos }px)`,
            };
        },
    },

    watch: {
        collapseOffset() {
            this.onUpdate();
        },

        toolbarVisibility: {
            handler() {
                this.onUpdate();
            },
            deep: true,
        },
    },

    mounted() {
        this.setPosition();
        this.setHeightListener();
        this.getDimensions();
        this.onUpdate();
    },

    beforeDestroy() {
        window.removeEventListener('resize', () => {
            this.$el.style.height = this.getHeight();
        });
    },

    methods: {
        ...mapMutations({
            toggleToolbar: TOGGLE_TOOLBAR,
        }),

        getHeight() {
            const header = document.getElementById('header');
            return `${ Math.max(document.documentElement.clientHeight, window.innerHeight || 0) - header.offsetHeight }px`;
        },

        getDimensions() {
            this.width = this.$refs.toolbar.offsetWidth;
            this.borderWidth = this.$refs.toolbar.offsetWidth - this.$refs.toolbar.clientWidth;
            this.handleWidth = this.$refs.toolbarHandle.offsetWidth;
        },

        setHeightListener() {
            this.$el.style.height = this.getHeight();
            window.addEventListener('resize', () => {
                this.$el.style.height = this.getHeight();
            });
        },

        setPosition() {
            const header = document.getElementById('header');
            const toolbar = this.$refs.toolbar;

            toolbar.style.top = `${ header.offsetHeight }px`;
        },

        onToggle() {
            this.toggleToolbar({ id: this.id });

            // wait until the animation finishes
            window.setTimeout(() => {
                this.$root.$emit('flex::toolbar::collapse');
            }, 333);
        },

        onUpdate() {
            this.$root.$emit('flex::toolbar::update', {
                id: this.id,
                dimensions: {
                    width: this.width,
                    borderWidth: this.borderWidth,
                    handleWidth: this.handleWidth,
                    collapseOffset: this.collapseOffset,
                },
            });
        },
    },
};
</script>

<style lang="scss">
.toolbar {
    $this: &;
    color: var(--sidebar-color);
    background-color: var(--sidebar-backgroundColor);
    border-color: var(--sidebar-handleBackgroundColor);
    position: fixed;
    top: 0;
    bottom: 0;
    border-style: solid;
    transition: all 0.33s;
    z-index: 101;
    outline: 1px solid var(--sidebar-handleBorderColor);

    &--left {
        left: 0;
        border-right-width: 5px;
        width: 320px;
    }

    &--right {
        right: 0;
        border-left-width: 5px;
        width: 360px;
    }

    // prevents the client font family from being inherited from custom client base styles
    * {
        font-family: $default-font-family;
    }

    &__collapse {
        @extend %middle;
        color: var(--sidebar-handleColor);
        background-color: var(--sidebar-handleBackgroundColor);
        font-size: 18px;
        padding-top: 10px;
        padding-bottom: 10px;
        border: 1px solid var(--sidebar-handleBorderColor);

        svg {
            position: relative;
        }

        #{ $this }--left & {
            right: -19px;
            border-left: none;
            padding-right: 6px;

            svg {
                right: -2px;
            }
        }

        #{ $this }--left.#{ $is-visible } & {
            svg {
                right: -2px;
            }
        }

        #{ $this }--right & {
            left: -19px;
            border-right: none;
            padding-left: 6px;

            svg {
                left: -2px;
            }
        }

        #{ $this }--right.#{ $is-visible } & {
            svg {
                left: -2px;
            }
        }
    }

    &__content {
        @extend %fill;
    }
}
</style>
